import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import './companyregister.css';

const CompanyRegister = () => {
    const [searchParams] = useSearchParams();
    const [selectedTalent, setSelectedTalent] = useState(null);
    const [formData, setFormData] = useState({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        companySize: '',
        industry: '',
        website: '',
        description: '',
        hiringNeeds: '',
        budget: '',
        timeline: '',
        agreeToTerms: false
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    useEffect(() => {
        const slug = searchParams.get('talent_slug');
        const name = searchParams.get('talent_name');
        const title = searchParams.get('talent_title');

        if (slug && name) {
            const talent = { slug, name, title: title || '' };
            setSelectedTalent(talent);
            localStorage.setItem('selectedTalent', JSON.stringify(talent));
            return;
        }

        const saved = localStorage.getItem('selectedTalent');
        if (saved) {
            try {
                setSelectedTalent(JSON.parse(saved));
            } catch {
                localStorage.removeItem('selectedTalent');
            }
        }
    }, [searchParams]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const payload = {
                ...formData,
                talent_slug: selectedTalent?.slug || '',
                talent_name: selectedTalent?.name || '',
                talent_title: selectedTalent?.title || '',
            };

            let companyId = null;

            // Store company data in localStorage for payment page
            localStorage.setItem('companyRegistrationData', JSON.stringify({
                ...formData,
                selectedTalent,
                companyId: null,
            }));

            try {
                const response = await fetch('https://server.abiatechhub.com/register-company', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

                const result = await response.json();
                if (result.success) {
                    companyId = result.data?.company_id || null;
                    localStorage.setItem('companyRegistrationData', JSON.stringify({
                        ...formData,
                        selectedTalent,
                        companyId,
                        talent_slug: selectedTalent?.slug || '',
                        talent_name: selectedTalent?.name || '',
                        talent_title: selectedTalent?.title || '',
                    }));
                }
            } catch (apiError) {
                console.log('API call failed, continuing with localStorage:', apiError);
            }
            
            // Simulate API call
            setTimeout(() => {
                setIsSubmitting(false);
                setSubmitSuccess(true);
            }, 2000);
        } catch (error) {
            console.error('Registration error:', error);
            setIsSubmitting(false);
        }
    };

    if (submitSuccess) {
        return (
            <div className="company-register">
                <div className="container py-5">
                    <div className="row justify-content-center">
                        <div className="col-lg-8">
                            <div className="success-message text-center">
                                <div className="success-icon mb-4">
                                    <i className="fas fa-check-circle"></i>
                                </div>
                                <h2 className="mb-3">Registration Successful!</h2>
                                <p className="lead mb-4">
                                    Thank you for registering with Abia Tech Hub Academy Talent Marketplace. 
                                    Our team will review your application and contact you within 24 hours.
                                </p>
                                <div className="next-steps">
                                    <h4 className="mb-3">What happens next?</h4>
                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="step-item">
                                                <div className="step-number">1</div>
                                                <h5>Application Review</h5>
                                                <p>We'll review your company details and hiring needs</p>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="step-item">
                                                <div className="step-number">2</div>
                                                <h5>Account Setup</h5>
                                                <p>We'll create your company account and send login details</p>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="step-item">
                                                <div className="step-number">3</div>
                                                <h5>Start Hiring</h5>
                                                <p>Access our talent pool and start connecting with graduates</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <Link to="/talent-payment" className="btn btn-ath-purple btn-lg me-3">
                                        Choose Plan & Pay
                                    </Link>
                                    <Link to="/talent-marketplace" className="btn btn-ath-outline btn-lg">
                                        Browse Talents
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="company-register">
            {/* Hero Section */}
            <section className="register-hero py-5">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-8 text-center">
                            <h1 className="display-4 fw-bold mb-3">Join Our Talent Marketplace</h1>
                            <p className="lead mb-4">
                                Connect with verified tech graduates from Abia Tech Hub Academy. 
                                Find the perfect talent for your team and grow your business.
                            </p>
                            {selectedTalent && (
                                <div className="alert alert-light border mb-4" role="status">
                                    <i className="fas fa-user-check me-2" style={{ color: '#8A038C' }}></i>
                                    You are registering interest in hiring{' '}
                                    <strong>{selectedTalent.name}</strong>
                                    {selectedTalent.title ? ` (${selectedTalent.title})` : ''}.
                                </div>
                            )}
                            <div className="benefits-list">
                                <div className="row">
                                    <div className="col-md-4">
                                        <div className="benefit-item">
                                            <i className="fas fa-users"></i>
                                            <h5>Verified Talents</h5>
                                            <p>Pre-screened graduates with proven skills</p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="benefit-item">
                                            <i className="fas fa-clock"></i>
                                            <h5>Quick Matching</h5>
                                            <p>Find the right talent in 24 hours</p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="benefit-item">
                                            <i className="fas fa-shield-alt"></i>
                                            <h5>Quality Guarantee</h5>
                                            <p>Industry-standard training and certification</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Registration Form */}
            <section className="register-form-section py-5">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-8">
                            <div className="form-container">
                                <div className="form-header text-center mb-4">
                                    <h2>Company Registration</h2>
                                    <p className="text-muted">Tell us about your company and hiring needs</p>
                                </div>

                                <form onSubmit={handleSubmit} className="registration-form">
                                    {/* Company Information */}
                                    <div className="form-section">
                                        <h4 className="section-title">Company Information</h4>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group mb-3">
                                                    <label htmlFor="companyName" className="form-label">Company Name *</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="companyName"
                                                        name="companyName"
                                                        value={formData.companyName}
                                                        onChange={handleInputChange}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group mb-3">
                                                    <label htmlFor="contactPerson" className="form-label">Contact Person *</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="contactPerson"
                                                        name="contactPerson"
                                                        value={formData.contactPerson}
                                                        onChange={handleInputChange}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group mb-3">
                                                    <label htmlFor="email" className="form-label">Work Email *</label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        id="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleInputChange}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group mb-3">
                                                    <label htmlFor="phone" className="form-label">Phone Number *</label>
                                                    <input
                                                        type="tel"
                                                        className="form-control"
                                                        id="phone"
                                                        name="phone"
                                                        value={formData.phone}
                                                        onChange={handleInputChange}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group mb-3">
                                                    <label htmlFor="companySize" className="form-label">Company Size *</label>
                                                    <select
                                                        className="form-select"
                                                        id="companySize"
                                                        name="companySize"
                                                        value={formData.companySize}
                                                        onChange={handleInputChange}
                                                        required
                                                    >
                                                        <option value="">Select company size</option>
                                                        <option value="1-10">1-10 employees</option>
                                                        <option value="11-50">11-50 employees</option>
                                                        <option value="51-200">51-200 employees</option>
                                                        <option value="201-500">201-500 employees</option>
                                                        <option value="500+">500+ employees</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group mb-3">
                                                    <label htmlFor="industry" className="form-label">Industry *</label>
                                                    <select
                                                        className="form-select"
                                                        id="industry"
                                                        name="industry"
                                                        value={formData.industry}
                                                        onChange={handleInputChange}
                                                        required
                                                    >
                                                        <option value="">Select industry</option>
                                                        <option value="Technology">Technology</option>
                                                        <option value="Finance">Finance</option>
                                                        <option value="Healthcare">Healthcare</option>
                                                        <option value="Education">Education</option>
                                                        <option value="E-commerce">E-commerce</option>
                                                        <option value="Manufacturing">Manufacturing</option>
                                                        <option value="Consulting">Consulting</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="form-group mb-3">
                                            <label htmlFor="website" className="form-label">Company Website</label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                id="website"
                                                name="website"
                                                value={formData.website}
                                                onChange={handleInputChange}
                                                placeholder="https://yourcompany.com"
                                            />
                                        </div>

                                        <div className="form-group mb-3">
                                            <label htmlFor="description" className="form-label">Company Description</label>
                                            <textarea
                                                className="form-control"
                                                id="description"
                                                name="description"
                                                rows="3"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                placeholder="Tell us about your company..."
                                            ></textarea>
                                        </div>
                                    </div>

                                    {/* Hiring Needs */}
                                    <div className="form-section">
                                        <h4 className="section-title">Hiring Needs</h4>
                                        <div className="form-group mb-3">
                                            <label htmlFor="hiringNeeds" className="form-label">What roles are you looking to fill? *</label>
                                            <textarea
                                                className="form-control"
                                                id="hiringNeeds"
                                                name="hiringNeeds"
                                                rows="3"
                                                value={formData.hiringNeeds}
                                                onChange={handleInputChange}
                                                placeholder="e.g., Frontend Developer, UI/UX Designer, Data Analyst..."
                                                required
                                            ></textarea>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group mb-3">
                                                    <label htmlFor="budget" className="form-label">Budget Range</label>
                                                    <select
                                                        className="form-select"
                                                        id="budget"
                                                        name="budget"
                                                        value={formData.budget}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="">Select budget range</option>
                                                                                                <option value="under-100000">Under ₦100,000/month</option>
                                        <option value="100000-500000">₦100,000 - ₦500,000/month</option>
                                        <option value="500000-1000000">₦500,000 - ₦1,000,000/month</option>
                                        <option value="1000000+">₦1,000,000+/month</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group mb-3">
                                                    <label htmlFor="timeline" className="form-label">Hiring Timeline</label>
                                                    <select
                                                        className="form-select"
                                                        id="timeline"
                                                        name="timeline"
                                                        value={formData.timeline}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="">Select timeline</option>
                                                        <option value="immediate">Immediate (within 1 month)</option>
                                                        <option value="1-3months">1-3 months</option>
                                                        <option value="3-6months">3-6 months</option>
                                                        <option value="6months+">6+ months</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Terms and Conditions */}
                                    <div className="form-section">
                                        <div className="form-check mb-4">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="agreeToTerms"
                                                name="agreeToTerms"
                                                checked={formData.agreeToTerms}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            <label className="form-check-label" htmlFor="agreeToTerms">
                                                I agree to the <Link to="/terms" target="_blank">Terms of Service</Link> and <Link to="/privacy" target="_blank">Privacy Policy</Link> *
                                            </label>
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <div className="form-actions text-center">
                                        <button
                                            type="submit"
                                            className="btn btn-ath-purple btn-lg px-5"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Submitting...
                                                </>
                                            ) : (
                                                'Submit Registration'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section className="pricing-section py-5 bg-light">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            <div className="text-center mb-5">
                                <h2>Simple, Transparent Pricing</h2>
                                <p className="lead text-muted">Choose the plan that works best for your hiring needs</p>
                            </div>

                            <div className="row g-4">
                                <div className="col-lg-4">
                                    <div className="pricing-card">
                                        <div className="pricing-header">
                                            <h4>Starter</h4>
                                            <div className="price">
                                                <span className="currency">₦</span>
                                                <span className="amount">499,000</span>
                                                <span className="period">/month</span>
                                            </div>
                                        </div>
                                        <div className="pricing-features">
                                            <ul>
                                                <li><i className="fas fa-check"></i> Access to talent profiles</li>
                                                <li><i className="fas fa-check"></i> Basic search and filtering</li>
                                                <li><i className="fas fa-check"></i> Contact up to 5 talents per month</li>
                                                <li><i className="fas fa-check"></i> View up to 50 candidate profiles</li>
                                                <li><i className="fas fa-check"></i> Email support</li>
                                                <li><i className="fas fa-check"></i> Monthly talent digest</li>
                                                <li><i className="fas fa-check"></i> Company profile listing</li>
                                            </ul>
                                        </div>
                                        <div className="pricing-footer">
                                            <Link to="/talent-payment" className="btn w-100" style={{ color: '#8A038C', borderColor: '#8A038C' }}>Get Started</Link>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-4">
                                    <div className="pricing-card featured">
                                        <div className="pricing-badge">Most Popular</div>
                                        <div className="pricing-header">
                                            <h4>Professional</h4>
                                            <div className="price">
                                                <span className="currency">₦</span>
                                                <span className="amount">999,000</span>
                                                <span className="period">/month</span>
                                            </div>
                                        </div>
                                        <div className="pricing-features">
                                            <ul>
                                                <li><i className="fas fa-check"></i> Everything in Starter</li>
                                                <li><i className="fas fa-check"></i> Advanced search filters</li>
                                                <li><i className="fas fa-check"></i> Contact unlimited talents</li>
                                                <li><i className="fas fa-check"></i> View unlimited candidate profiles</li>
                                                <li><i className="fas fa-check"></i> Priority support</li>
                                                <li><i className="fas fa-check"></i> Talent matching service</li>
                                                <li><i className="fas fa-check"></i> Saved searches & alerts</li>
                                                <li><i className="fas fa-check"></i> Interview scheduling tools</li>
                                                <li><i className="fas fa-check"></i> Basic analytics dashboard</li>
                                            </ul>
                                        </div>
                                        <div className="pricing-footer">
                                            <Link to="/talent-payment" className="btn w-100" style={{ backgroundColor: '#8A038C', borderColor: '#8A038C', color: 'white' }}>Get Started</Link>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-4">
                                    <div className="pricing-card pricing-card-enterprise">
                                        <div className="pricing-header">
                                            <h4>Enterprise</h4>
                                            <div className="price">
                                                <span className="currency">₦</span>
                                                <span className="amount">1,999,000</span>
                                                <span className="period">/month</span>
                                            </div>
                                        </div>
                                        <div className="pricing-features">
                                            <ul>
                                                <li><i className="fas fa-check"></i> Everything in Professional</li>
                                                <li><i className="fas fa-check"></i> Dedicated account manager</li>
                                                <li><i className="fas fa-check"></i> Custom talent sourcing</li>
                                                <li><i className="fas fa-check"></i> API access & integrations</li>
                                                <li><i className="fas fa-check"></i> 24/7 phone support</li>
                                                <li><i className="fas fa-check"></i> Custom reports & analytics</li>
                                                <li><i className="fas fa-check"></i> Bulk hiring & team seats</li>
                                                <li><i className="fas fa-check"></i> Onboarding & training</li>
                                                <li><i className="fas fa-check"></i> SLA & contract support</li>
                                            </ul>
                                        </div>
                                        <div className="pricing-footer">
                                            <Link to="/contact" className="btn w-100" style={{ color: '#8A038C', borderColor: '#8A038C' }}>Contact Sales</Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CompanyRegister;
